import { useCallback, useRef } from "react";
import { useDispatch } from "react-redux";

import { AppDispatch } from "../../../store";
import {
  resetCalculatedCart,
  setCalculatedCart,
  setCartCalculating,
  setPromoInvalid,
} from "../../../store/slices/basketSlice";
import { basketAPI } from "../api/basketAPI";
import {
  getCalculateCartCouponStatus,
  parseCalculateCartResult,
} from "../libs/calculateCartAdapter";
import { CalculateCartRequest } from "../types/CalculateCart";

export const useCalculateCart = () => {
  const dispatch = useDispatch<AppDispatch>();
  const abortControllerRef = useRef<AbortController | null>(null);

  const calculateCart = useCallback(
    async (
      body: CalculateCartRequest,
      options?: { validatePromo?: boolean },
    ) => {
      if (!body.positions.length) {
        dispatch(resetCalculatedCart());
        return null;
      }

      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      const controller = new AbortController();
      abortControllerRef.current = controller;

      dispatch(setCartCalculating(true));

      try {
        const response = await basketAPI.calculateCart(body, controller.signal);
        const result = parseCalculateCartResult(response.data, body.promoCode);

        if (result) {
          const fallbackSubtotal = Math.max(
            0,
            body.totalPrice - body.deliveryCost,
          );

          dispatch(
            setCalculatedCart({
              ...result,
              subtotalPrice: result.subtotalPrice || fallbackSubtotal,
              deliveryPrice: result.deliveryPrice || body.deliveryCost,
              totalPrice: result.totalPrice || body.totalPrice,
            }),
          );
        } else {
          if (options?.validatePromo) {
            dispatch(
              setPromoInvalid({
                couponStatus: getCalculateCartCouponStatus(response.data),
              }),
            );
          } else {
            dispatch(setCartCalculating(false));
          }
        }

        return result;
      } catch (error: any) {
        dispatch(setCartCalculating(false));
        if (error?.name === 'AbortError') {
          return null;
        }
        if (options?.validatePromo) {
          dispatch(setPromoInvalid(undefined));
        }
        return null;
      }
    },
    [dispatch],
  );

  return { calculateCart };
};
